import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Package, Wrench, WarningOctagon, Stack, ArrowUpRight } from "@phosphor-icons/react";

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 backdrop-blur-sm relative overflow-hidden group transition-all duration-200 hover:border-zinc-700">
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#F5A623] rounded-full blur-3xl opacity-0 group-hover:opacity-[0.08] transition-opacity duration-500" />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</div>
          <div className="mt-2 text-4xl font-bold font-mono text-zinc-100">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-md grid place-items-center ${accent}`}>
          <Icon size={20} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [recent, setRecent] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, m, r] = await Promise.all([
          api.get("/inventory/stats"),
          api.get("/inventory/maintenance/upcoming?days=30"),
          api.get("/inventory?"),
        ]);
        setStats(s.data);
        setUpcoming(m.data.slice(0, 6));
        setRecent(r.data.slice(0, 5));
      } catch {}
    })();
  }, []);

  return (
    <div className="space-y-8 animate-fade-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">Overview</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Operations <span className="text-[#F5A623]">Dashboard</span>
          </h1>
          <p className="mt-2 text-sm text-zinc-500 max-w-xl">
            Live snapshot of every asset across the KERMA GAMES facility. Monitor, tag, and keep your gear serviced.
          </p>
        </div>
        <button
          onClick={() => nav("/inventory/new")}
          data-testid="dashboard-add-item-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors"
        >
          + Add Asset
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testid="stat-total"
          label="Total Assets"
          value={stats?.total_items ?? "—"}
          icon={Package}
          accent="bg-zinc-800 text-zinc-300"
        />
        <StatCard
          testid="stat-categories"
          label="Categories"
          value={stats?.categories?.length ?? "—"}
          icon={Stack}
          accent="bg-zinc-800 text-zinc-300"
        />
        <StatCard
          testid="stat-upcoming"
          label="Upcoming Maintenance"
          value={stats?.upcoming_maintenance ?? "—"}
          icon={Wrench}
          accent="bg-[#F5A623]/15 text-[#F5A623]"
        />
        <StatCard
          testid="stat-overdue"
          label="Overdue Maintenance"
          value={stats?.overdue_maintenance ?? "—"}
          icon={WarningOctagon}
          accent="bg-red-500/10 text-red-400"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Categories breakdown */}
        <div className="lg:col-span-1 p-6 rounded-lg bg-zinc-900/50 border border-zinc-800">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-4">By Category</div>
          <div className="space-y-3">
            {(stats?.categories || []).slice(0, 8).map((c) => {
              const total = stats?.total_items || 1;
              const pct = Math.max(2, Math.round((c.count / total) * 100));
              return (
                <div key={c.category} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-300">{c.category}</span>
                    <span className="font-mono text-zinc-500">{c.count}</span>
                  </div>
                  <div className="h-1 rounded-full bg-zinc-800 overflow-hidden">
                    <div className="h-full bg-[#F5A623]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {(!stats?.categories || stats.categories.length === 0) && (
              <div className="text-sm text-zinc-600">No assets yet.</div>
            )}
          </div>
        </div>

        {/* Upcoming maintenance */}
        <div className="lg:col-span-2 p-6 rounded-lg bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Upcoming / Overdue Maintenance</div>
            <button onClick={() => nav("/maintenance")} className="text-xs text-[#F5A623] hover:underline inline-flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </button>
          </div>
          <div className="divide-y divide-zinc-800/70">
            {upcoming.length === 0 && <div className="text-sm text-zinc-600 py-6 text-center">All clear. No maintenance due.</div>}
            {upcoming.map((item) => {
              const due = item.next_maintenance ? new Date(item.next_maintenance) : null;
              const overdue = due && due < new Date();
              return (
                <div key={item.id} className="flex items-center justify-between py-3 hover:bg-zinc-900/50 px-2 -mx-2 rounded transition-colors cursor-pointer" onClick={() => nav(`/inventory/${item.id}`)}>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{item.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{item.sku} · {item.category}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-mono ${overdue ? "text-red-400" : "text-[#F5A623]"}`}>
                      {due ? due.toLocaleDateString() : "—"}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-600">{overdue ? "Overdue" : "Upcoming"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent additions */}
      <div className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Recent Additions</div>
          <button onClick={() => nav("/inventory")} className="text-xs text-[#F5A623] hover:underline inline-flex items-center gap-1">
            View inventory <ArrowUpRight size={12} />
          </button>
        </div>
        <div className="divide-y divide-zinc-800/70">
          {recent.length === 0 && <div className="text-sm text-zinc-600 py-8 text-center">No assets have been added yet.</div>}
          {recent.map((i) => (
            <div key={i.id} className="flex items-center gap-4 py-3 cursor-pointer hover:bg-zinc-900/40 px-2 -mx-2 rounded" onClick={() => nav(`/inventory/${i.id}`)}>
              <div className="h-10 w-10 rounded-md bg-zinc-800 overflow-hidden grid place-items-center flex-shrink-0">
                {i.photo ? <img src={i.photo} alt={i.name} className="h-full w-full object-cover" /> : <Package size={16} className="text-zinc-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">{i.name}</div>
                <div className="text-xs text-zinc-500 font-mono">{i.sku}</div>
              </div>
              <div className="text-xs text-zinc-500 font-mono hidden sm:block">{i.location}</div>
              <div className="text-xs text-zinc-600 font-mono">
                {new Date(i.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
